// @flow
import * as React from 'react';
import Box from 'components/Box';
import Flex from 'components/Flex';
import Icon from 'components/Icon';
import Button from 'components/Button';
import styled from 'styled-components';
import ClientOnly from 'containers/ClientOnly';
import { getJson } from '../utils/http';
import { transition, colors } from '../theme';

const SERVICES: Object = {
  youtube: {
    urlPattern: /youtube|youtu\.be/,
    idPattern: /(youtu\.be\/|\.com\/v\/|.com\/watch\?v=)([\w-]+)/,
    getSrc: ({ videoId }) =>
      `https://www.youtube.com/embed/${videoId}?enablejsapi=1&origin=${
        typeof window !== 'undefined' ? window.location.origin : ''
      }`,
    getImage: ({ videoId }) =>
      `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
    scriptSrc: '//www.youtube.com/player_api'
  },
  vimeo: {
    urlPattern: /vimeo/,
    idPattern: /(vimeo\.com\/video\/|vimeo\.com\/)([0-9]+)/,
    getSrc: ({ videoId }) =>
      `https://player.vimeo.com/video/${videoId}?api=1&muted=1`,
    getImage: ({ videoImageId }) =>
      `https://i.vimeocdn.com/video/${videoImageId}.webp?mw=1200&mh=675&q=70`,
    scriptSrc: '//player.vimeo.com/api/player.js'
  }
};

const getIframeId = ({
  prefix,
  videoId
}: {
  prefix: string,
  videoId: string
}) => `${prefix}-video-${videoId}`;

const getImage = ({
  videoId,
  videoType
}: {
  videoId: string,
  videoType: string
}) => {
  const protocol =
    typeof window !== 'undefined' ? window.location.protocol : 'https:';
  return SERVICES[videoType].getImage({ videoId, protocol });
};

const Iframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 0;
`;

export const PlayButton = styled(
  ({
    className,
    asButton,
    smallButton,
    ...props
  }: {
    className: string,
    asButton?: boolean,
    smallButton?: boolean
  }) => {
    const Component = asButton ? Button : 'div';
    return (
      <Component className={className} {...props}>
        <Icon name="play-button" />
      </Component>
    );
  }
)`
  svg {
    width: ${({ smallButton }) => (smallButton ? 60 : 120)}px;
    height: ${({ smallButton }) => (smallButton ? 60 : 120)}px;
    * {
      transition: all ${transition};
    }
  }
  &:hover {
    circle {
      fill: white;
    }
    path,
    polyline {
      fill: ${colors.waterBlue};
    }
  }
`;

const ClickOverlay = styled(
  ({
    className,
    onClick,
    isReady,
    noPlayer,
    smallButton,
    hideButton
  }: {
    className: string,
    onClick: Function,
    isReady: boolean,
    noPlayer?: boolean,
    smallButton?: boolean,
    hideButton?: boolean
  }) => (
    <Flex
      alignItems="center"
      justifyContent="center"
      width="100%"
      height="100%"
      position="absolute"
      top="0"
      left="0"
      className={className}
    >
      {!hideButton && (
        <PlayButton
          onClick={onClick}
          asButton={!noPlayer}
          disabled={!isReady}
          smallButton={smallButton}
        />
      )}
    </Flex>
  )
)`
  background-image: url('${props => props.image}');
  background-size: cover;
  background-position: center;
  transition: all ${transition};
  ${props =>
    props.isReady ||
    `
    background-color: rgba(0,0,0,0.3);
    button {
      opacity: 0.3;
    }
  `}
  ${props =>
    (!props.image || props.image.length === 0) &&
    `
    background-color: ${colors.silver}
  `}
`;

export const AmpVideo = ({ url }: { url: string }) => {
  const videoType =
    Object.keys(SERVICES).find(type => SERVICES[type].urlPattern.test(url)) ||
    'youtube';
  const VideoComponent = `amp-${videoType}`;

  const videoId = (() => {
    if (videoType && videoType.length > 0) {
      const match = url.match(SERVICES[videoType].idPattern);
      return match && match[2] ? match[2] : '';
    }
    return '';
  })();
  return (
    <VideoComponent
      data-videoid={videoId}
      layout="responsive"
      width="480"
      height="270"
    />
  );
};

type Props = {
  url: string,
  fill?: boolean,
  disabled?: boolean,
  prefix?: string,
  noPlayer?: boolean,
  smallButton?: boolean,
  hideButton?: boolean
};

const Video = ({
  url,
  fill,
  disabled,
  prefix = '',
  noPlayer,
  smallButton,
  hideButton,
  ...props
}: Props) => {
  const { useState, useEffect, useRef, useCallback } = React;

  const [image, setImage] = useState('');
  const [playVideo, setPlayVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const iframeEl = useRef(null);

  const videoType =
    Object.keys(SERVICES).find(type => SERVICES[type].urlPattern.test(url)) ||
    '';

  const videoId = (() => {
    if (videoType && videoType.length > 0) {
      const match = url.match(SERVICES[videoType].idPattern);
      return match && match[2] ? match[2] : '';
    }
    return '';
  })();

  const protocol =
    typeof window !== 'undefined' ? window.location.protocol : 'https:';
  const src = SERVICES[videoType]
    ? SERVICES[videoType].getSrc({ videoId, protocol })
    : '';

  const setupPlayer = useCallback(
    () => {
      if (!isReady) {
        setIsReady(true);
      } else if (
        !playVideo &&
        !noPlayer &&
        videoType === 'vimeo' &&
        iframeEl &&
        iframeEl.current &&
        typeof window !== 'undefined' &&
        window.Vimeo
      ) {
        const player = new window.Vimeo.Player(
          getIframeId({ prefix, videoId })
        );
        setPlayVideo(() => () => {
          setIsPlaying(true);
          player.play();
        });
      }
    },
    [isReady, noPlayer, playVideo, prefix, videoId, videoType]
  );

  useEffect(
    () => {
      const createScript = ({ onLoad }: { onLoad?: Function } = {}) => {
        const script = document.createElement('script');
        if (onLoad) script.onload = onLoad;
        script.src = SERVICES[videoType].scriptSrc;
        const firstScriptTag = document.getElementsByTagName('script')[0];
        if (firstScriptTag && firstScriptTag.parentNode) {
          firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
        }
      };

      if (videoType && videoType.length > 0) {
        let newImage;
        if (videoType === 'vimeo') {
          getJson(`//vimeo.com/api/oembed.json?url=${src}`).then(res => {
            const videoImageId = res.thumbnail_url.match(
              /vimeocdn\.com\/video\/([0-9]+)_/
            )[1];
            setImage(SERVICES.vimeo.getImage({ videoImageId }));
          });
          newImage = '';
        } else {
          newImage = getImage({ videoType, videoId });
        }

        setImage(newImage);
        if (SERVICES[videoType] && SERVICES[videoType].scriptSrc) {
          if (videoType === 'youtube') {
            setupPlayer();
          } else if (videoType === 'vimeo') {
            if (window && typeof window.Vimeo === 'undefined') {
              createScript({
                onLoad: () => setupPlayer(),
                videoType
              });
            } else {
              setupPlayer();
            }
          }
        } else {
          setIsReady(true);
          setIsPlaying(true);
        }
      }
    },
    [isReady, setupPlayer, src, videoId, videoType]
  );

  useEffect(
    () => {
      setupPlayer();
    },
    [setupPlayer]
  );

  return (
    <Box
      position="relative"
      pb={fill ? '0' : '56.25%'}
      height={fill ? '100%' : 'auto'}
      {...props}
    >
      <ClientOnly>
        {!noPlayer && (
          <Iframe
            data-qa-id={`${prefix}-video`}
            src={src}
            frameBorder="0"
            id={getIframeId({
              prefix,
              videoId
            })}
            ref={iframeEl}
            title="Video"
          />
        )}
        {!isPlaying && (videoType !== 'youtube' || noPlayer) && (
          <ClickOverlay
            image={image}
            onClick={disabled ? () => undefined : playVideo}
            isReady={isReady}
            disabled={disabled || noPlayer}
            smallButton={smallButton}
            hideButton={hideButton}
            noPlayer={noPlayer}
          />
        )}
      </ClientOnly>
    </Box>
  );
};

export default Video;
